import * as React from 'react';
import { Upload } from 'lucide-react';
import {
  type FileMetadata,
  Uploader,
  type UploaderFile,
  type UploaderProps,
} from './Uploader';
import { cn } from '../../../cn';
import {
  type BaseUploadFileItem,
  convertFile,
  type FileType,
  getAcceptString,
  DEFAULT_ACCEPTED_FILE_TYPES,
  DEFAULT_MAX_FILE_SIZE,
  type BaseUploadingFileItem,
  validateFile,
} from './file';
import { useMergeState } from '../../../hooks/useMergeState';
import { type ReactNode, useMemo, useRef, useState } from 'react';
import { showError } from '../../../utils/toast';
import { FileItem } from './FileItem';
import { UploadingFileItem } from './UploadingFileItem';

type Actions = {
  removeFile: (file: BaseUploadFileItem, index: number) => void;
  cancelUploadFile: (file: BaseUploadingFileItem, index: number) => void;
  downloadFile: (file: BaseUploadFileItem, index: number) => void;
};

export interface BaseUploadProps {
  value?: BaseUploadFileItem[];
  onChange?: (files: BaseUploadFileItem[]) => void;
  defaultValue?: BaseUploadFileItem[];
  accept?: FileType[];
  maxSize?: number; // in bytes
  maxFiles?: number;
  showPreview?: boolean;
  onError?: (error: string) => void;
  uploaderProps?: Omit<UploaderProps, 'children' | 'accept' | 'disabled'>;
  children?: UploaderProps['children'];
  containerClassName?: string;
  disabled?: boolean;
  renderFileItem?: (
    file: BaseUploadFileItem,
    index: number,
    actions: Actions,
  ) => ReactNode;
  renderUploadingFileItem?: (
    file: BaseUploadingFileItem,
    index: number,
    actions: Actions,
  ) => ReactNode;
  uploadFile?: (
    file: File,
    onProgress: (progress: number) => void,
  ) => Promise<FileMetadata> | FileMetadata;
  errorHandlingStrategy?: 'toast' | 'inline';
  events?: {
    onRemoveFile?: (file: BaseUploadFileItem, index: number) => void;
    onCancelUploadFile?: (file: BaseUploadingFileItem, index: number) => void;
    onDownloadFile?: (file: BaseUploadFileItem, index: number) => void;
  };
}

export function BaseUpload({
  value,
  accept = DEFAULT_ACCEPTED_FILE_TYPES,
  maxSize = DEFAULT_MAX_FILE_SIZE,
  maxFiles,
  showPreview = true,
  defaultValue,
  children,
  uploaderProps,
  containerClassName,
  disabled,
  errorHandlingStrategy = 'toast',
  onChange,
  uploadFile,
  renderFileItem,
  renderUploadingFileItem,
  events,
}: BaseUploadProps) {
  const [uploadingFiles, setUploadingFiles] =
    useState<BaseUploadingFileItem[]>();

  const cancelledFiles = useRef<BaseUploadingFileItem[]>();

  const { onChange: _onChange, value: _value } = useMergeState({
    value,
    onChange,
    defaultValue: defaultValue ?? [],
  });

  const acceptString = getAcceptString(accept);

  const handleFilesChange = React.useCallback(
    (files: UploaderFile[]) => {
      const errors: string[] = files.filter(f => !!f.error).map(f => f.error);
      if (errors.length && errorHandlingStrategy === 'toast') {
        errors.forEach(e => showError(e));
      }

      if (!files || files.length === 0) {
        return;
      }

      const newFiles = (
        errorHandlingStrategy === 'toast' ? files.filter(f => !f.error) : files
      ).filter(
        f =>
          !cancelledFiles.current?.some(
            cancelledFile =>
              cancelledFile.fileName === f.name &&
              cancelledFile.lastModified === f.lastModified,
          ),
      );

      cancelledFiles.current = undefined;

      if (newFiles.length === 0) {
        return;
      }

      _onChange?.([...(_value ?? []), ...newFiles.map(convertFile)]);
    },
    [errorHandlingStrategy, _onChange, _value, cancelledFiles],
  );

  const actions = useMemo(
    () => ({
      removeFile: (file: BaseUploadFileItem, index: number) => {
        const newFiles = (_value ?? []).filter((_, i: number) => i !== index);
        _onChange?.(newFiles);
        events?.onRemoveFile?.(file, index);
      },
      cancelUploadFile: (file: BaseUploadingFileItem, index: number) => {
        setUploadingFiles(prev =>
          prev?.filter(
            f =>
              f.fileName !== file.fileName &&
              f.lastModified !== file.lastModified,
          ),
        );
        cancelledFiles.current = (cancelledFiles.current ?? []).concat(file);
        events?.onCancelUploadFile?.(file, index);
      },
      downloadFile: (file: BaseUploadFileItem, index: number) => {
        events?.onDownloadFile?.(file, index);
      },
    }),
    [_onChange, _value, events],
  );

  const handleProgress = (progress: number, file: File) => {
    // When completed, remove uploaded files from uploading files list
    if (progress === 100) {
      setUploadingFiles(prev => {
        return (prev ?? []).map(f =>
          f.fileName === file.name && f.lastModified === file.lastModified
            ? {
                ...f,
                progress: 100,
              }
            : f,
        );
      });

      setTimeout(() => {
        setUploadingFiles(prev =>
          prev?.filter(
            f =>
              f.fileName !== file.name && f.lastModified !== file.lastModified,
          ),
        );
      }, 300);
    } else {
      // Update existing progress
      setUploadingFiles(prev => {
        return (prev ?? []).map(f =>
          f.fileName === file.name && f.lastModified === file.lastModified
            ? {
                ...f,
                progress,
              }
            : f,
        );
      });
    }
  };

  const handleOnFile = async (file: File) => {
    const error = validateFile(file, { accept: acceptString, maxSize });

    if (error) {
      return { error };
    }

    if (uploadFile) {
      try {
        setUploadingFiles(prev =>
          (prev ?? []).concat({ ...convertFile(file), progress: 0 }),
        );
        const metadata = await uploadFile(file, progress =>
          handleProgress(progress, file),
        );

        if (metadata.error) {
          return {
            error: metadata.error,
          };
        }

        return metadata;
      } catch {
        return { error: 'Error while uploading file' };
      }
    }

    return {};
  };

  const _children = useMemo(() => {
    if (children) {
      return children;
    }
    return (
      <div className="flex flex-col gap-4 items-center">
        <Upload size={16} />
        <div className="text-center text-sm text-muted-foreground">
          <div>
            Supported file type: {accept.join(', ').toLocaleUpperCase()}.
          </div>
          <div>Maximum file size: {maxSize / 1024 / 1024}MB per file</div>
        </div>
      </div>
    );
  }, [accept, children, maxSize]);

  const beforeUpload = (files: File[]) => {
    if (maxFiles && [...(_value ?? []), ...files].length > maxFiles) {
      showError(`Maximum ${maxFiles} files is allowed`);
      return false;
    }

    return uploaderProps?.beforeUpload
      ? uploaderProps?.beforeUpload?.(files)
      : true;
  };

  const _renderFileItem = (file: BaseUploadFileItem, index: number) => {
    if (renderFileItem) {
      return renderFileItem(file, index, actions);
    }

    return (
      <FileItem
        key={`${file.fileName}-${file.lastModified}-${index}`}
        file={file}
        onRemoveFile={() => actions.removeFile(file, index)}
        disabled={disabled}
        onDownloadFile={() => actions.downloadFile(file, index)}
      />
    );
  };

  const _renderUploadingFileItem = (
    file: BaseUploadingFileItem,
    index: number,
  ) => {
    if (renderUploadingFileItem) {
      return renderUploadingFileItem(file, index, actions);
    }

    return (
      <UploadingFileItem
        file={file}
        onCancel={() => actions.cancelUploadFile(file, index)}
      />
    );
  };

  const files = [...(uploadingFiles ?? []), ...(_value ?? [])];

  return (
    <div className={cn('w-full', containerClassName)}>
      <Uploader
        {...uploaderProps}
        accept={acceptString}
        onEachFile={handleOnFile}
        beforeUpload={beforeUpload}
        onChange={handleFilesChange}
        disabled={disabled}
        children={_children}
      />

      {showPreview && (files?.length ?? 0) > 0 ? (
        <ul className="mt-4 space-y-2">
          {files?.map(
            (
              file: BaseUploadFileItem | BaseUploadingFileItem,
              index: number,
            ) => {
              if ('progress' in file) {
                return _renderUploadingFileItem(file, index);
              }

              return _renderFileItem(file, index);
            },
          )}
        </ul>
      ) : null}
    </div>
  );
}

BaseUpload.displayName = 'BaseUpload';
